import React from "react";
import {
  FileText, IndianRupee, Ban, Download, Printer, Eye, Pencil, Trash2, MoreHorizontal, CheckCircle2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatINR, formatNum } from "@/lib/api";
import { INVOICE_STYLE, INVOICE_LABELS, GOLD_STATUS_LABELS, GOLD_STATUS_STYLE, RUPEE_STATUS_LABELS, RUPEE_STATUS_STYLE, PAYMENT_STYLE } from "@/pages/admin/orders/orderConstants";

// The "Invoices" tab: only finalized (invoice_no assigned) records — payment status split
// into Metal (Gold) and Rupee so a partner who has settled the gold owed but not yet the
// making/GST rupee balance (or vice-versa) is represented accurately, plus tax details and
// the PDF download action. "Mark as Fully Settled" only appears once BOTH ledgers hit zero.
export const InvoicesTable = ({
  invoices, ordersLoading, selectedIds, toggleSelect, toggleSelectAll, allSelected, canAct, busy, staff,
  navigate, onCancel, onView, onPay, onEditInvoice, onDelete, onPrint, onDownload, onSettle,
}) => (
  <Card className="overflow-hidden">
    <div className="overflow-x-auto">
      <Table data-testid="admin-invoices-table">
        <TableHeader><TableRow className="bg-[#F4EBDD]">
          <TableHead className="w-10"><Checkbox data-testid="select-all-invoices" checked={allSelected} onCheckedChange={toggleSelectAll} /></TableHead>
          <TableHead>Invoice No</TableHead><TableHead>Invoice Date</TableHead><TableHead>Customer</TableHead>
          <TableHead>Mobile</TableHead><TableHead>GSTIN</TableHead><TableHead>Salesman</TableHead>
          <TableHead className="text-right">Total</TableHead><TableHead>Tax (GST)</TableHead>
          <TableHead>Metal Status</TableHead><TableHead>Rupee Status</TableHead><TableHead>Invoice</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {ordersLoading ? (
            <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
          ) : invoices.length === 0 ? (
            <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground" data-testid="invoices-empty-state">No invoices generated yet — use "Generate Invoice" on an order to finalize one.</TableCell></TableRow>
          ) : invoices.map((o) => {
            const allowed = canAct(o);
            const canSettle = allowed && !o.fully_settled && (o.rupee_status === "paid") && (o.gold_status === "paid" || o.gold_status === "not_applicable");
            const gst = o.tax_summary || {};
            return (
              <TableRow key={o.id} className="hover:bg-[#F7F0E6]" data-testid="invoice-row">
                <TableCell><Checkbox data-testid="select-invoice-checkbox" checked={selectedIds.includes(o.id)} onCheckedChange={() => toggleSelect(o.id)} /></TableCell>
                <TableCell className="font-mono text-xs">{o.invoice_no}</TableCell>
                <TableCell className="text-xs">{o.invoice_date || "—"}</TableCell>
                <TableCell className="text-sm max-w-[140px] truncate">{o.shop_name}</TableCell>
                <TableCell className="text-xs font-mono">{o.shop_phone}</TableCell>
                <TableCell className="text-xs font-mono">{o.shop_gstin || "—"}</TableCell>
                <TableCell className="text-xs">{o.salesman_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatINR(o.total)}</TableCell>
                <TableCell className="text-xs font-mono">{gst.gst_amount ? `${formatINR(gst.gst_amount)} (${formatNum(gst.gst_pct)}%)` : "—"}</TableCell>
                <TableCell>
                  <Badge data-testid="invoice-gold-status-badge" className={`${GOLD_STATUS_STYLE[o.gold_status] || ""} text-xs`}>{GOLD_STATUS_LABELS[o.gold_status] || o.gold_status}</Badge>
                </TableCell>
                <TableCell>
                  <Badge data-testid="invoice-rupee-status-badge" className={`${RUPEE_STATUS_STYLE[o.rupee_status] || ""} text-xs`}>{RUPEE_STATUS_LABELS[o.rupee_status] || o.rupee_status}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge data-testid="invoice-status-badge" className={`${INVOICE_STYLE[o.invoice_status]} text-xs w-fit`}>{INVOICE_LABELS[o.invoice_status]}</Badge>
                    {o.fully_settled && <Badge data-testid="invoice-fully-settled-badge" className={`${PAYMENT_STYLE.paid} text-xs w-fit gap-1`}><CheckCircle2 className="h-3 w-3" /> Settled</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {canSettle && (
                      <Button data-testid="invoice-mark-settled-button" size="sm" className="gap-1.5 h-7 px-2.5" disabled={busy} onClick={() => onSettle(o)}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark Fully Settled
                      </Button>
                    )}
                    {allowed && !["cancelled"].includes(o.invoice_status) && (
                      <Button data-testid="action-record-payment" size="sm" variant="secondary" className="gap-1 h-7 px-2" disabled={busy} onClick={() => onPay(o)}><IndianRupee className="h-3.5 w-3.5" /></Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid="invoice-actions-menu" size="icon" variant="ghost" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid="action-view-order" onClick={() => onView(o)}><Eye className="h-3.5 w-3.5 mr-2" /> View Order</DropdownMenuItem>
                        <DropdownMenuItem data-testid="action-view-invoice" onClick={() => navigate(`/crm/invoice/${o.id}`)}><FileText className="h-3.5 w-3.5 mr-2" /> View Invoice</DropdownMenuItem>
                        {o.invoice_status !== "cancelled" && (
                          <DropdownMenuItem data-testid="action-edit-invoice" onClick={() => onEditInvoice(o)}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit Invoice</DropdownMenuItem>
                        )}
                        <DropdownMenuItem data-testid="action-print-invoice" onClick={() => onPrint(o)}><Printer className="h-3.5 w-3.5 mr-2" /> Print Invoice</DropdownMenuItem>
                        <DropdownMenuItem data-testid="action-download-pdf" onClick={() => onDownload(o)}><Download className="h-3.5 w-3.5 mr-2" /> Download PDF</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {o.invoice_status !== "cancelled" && allowed && (
                          <DropdownMenuItem data-testid="action-cancel-invoice" onClick={() => onCancel(o)} className="text-destructive"><Ban className="h-3.5 w-3.5 mr-2" /> Cancel Invoice</DropdownMenuItem>
                        )}
                        {staff?.is_manager && (
                          <DropdownMenuItem data-testid="action-delete-invoice" onClick={() => onDelete(o)} className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Invoice</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </Card>
);
